/**
 * ISO 20022 Flow Simulator — scenario definitions.
 *
 * Each scenario plays out as a sequence of messages between lanes (actors).
 * A step either ORIGINATES a message (has a `payload` — the Flow Simulator
 * POSTs it to /api/generate/{msgType} to fetch the real engine output) or is
 * a FORWARDED hop (`sameAs` references an earlier step's generated XML, so the
 * switch "relays" the identical message).
 *
 * Every scenario threads one correlation key (`correlationId`) through its
 * messages — an EndToEndId for payments, a verification/process/reference id
 * for account lifecycle flows — so participants see how messages correlate.
 *
 * Scenarios are grouped for the picker via `category`; per-scenario `actors`
 * override the default payment lanes.
 */

export const FLOW_ACTORS = ['Payer DFSP', 'IIPS Switch', 'Payee DFSP'];

const ACCOUNT_ACTORS = ['Participant DFSP', 'IIPS Switch'];
const LOOKUP_ACTORS  = ['Requesting DFSP', 'IIPS Switch', 'Account-holding DFSP'];
const RTP_ACTORS     = ['Debtor DFSP', 'IIPS Switch', 'Creditor DFSP'];

/* ───────────────────────── Credit transfer ───────────────────────── */

const E2E = 'E2E-2026-0042';
const ORIG_MSG_ID = 'MSG-CT-2026-0042';

const PACS008_PAYLOAD = {
  grpHdr: {
    msgId: ORIG_MSG_ID,
    creDtTm: '2026-06-24T09:00:00Z',
    nbOfTxs: '1',
    sttlmInf: { sttlmMtd: 'CLRG' },
    instgAgt: { finInstnId: { othr: { id: '078' } } },
    instdAgt: { finInstnId: { othr: { id: '040' } } },
  },
  cdtTrfTxInf: [{
    pmtId: { instrId: 'INSTR-0042', endToEndId: E2E, txId: 'TX-2026-0042' },
    intrBkSttlmAmt: { value: '1000.00', ccy: 'RWF' },
    chrgBr: 'SLEV',
    dbtr: { nm: 'Alice Uwase' },
    dbtrAcct: { id: { othr: { id: '0788123456' } } },
    dbtrAgt: { finInstnId: { othr: { id: '078' } } },
    cdtrAgt: { finInstnId: { othr: { id: '040' } } },
    cdtr: { nm: 'Bob Mugisha' },
    cdtrAcct: { id: { othr: { id: '0720000042' } } },
  }],
};

const pacs002For = (txSts, extras = {}) => ({
  grpHdr: {
    msgId: 'STS-CT-0042',
    creDtTm: '2026-06-24T09:00:05Z',
    instgAgt: { finInstnId: { othr: { id: '040' } } },
    instdAgt: { finInstnId: { othr: { id: '078' } } },
  },
  orgnlGrpInfAndSts: { orgnlMsgId: ORIG_MSG_ID, orgnlMsgNmId: 'pacs.008.001.11' },
  txInfAndSts: [{
    orgnlInstrId: 'INSTR-0042',
    orgnlEndToEndId: E2E,
    orgnlTxId: 'TX-2026-0042',
    txSts,
    ...extras,
  }],
});

/* ───────────────────────── Payment return / reversal ─────────────── */

const PACS004_PAYLOAD = {
  grpHdr: {
    msgId: 'RTR-MSG-0042',
    creDtTm: '2026-06-25T10:00:00Z',
    nbOfTxs: '1',
    sttlmInf: { sttlmMtd: 'CLRG' },
    instgAgt: { finInstnId: { othr: { id: '040' } } },
    instdAgt: { finInstnId: { othr: { id: '078' } } },
  },
  orgnlGrpInf: { orgnlMsgId: ORIG_MSG_ID, orgnlMsgNmId: 'pacs.008.001.11' },
  txInf: [{
    rtrId: 'RTR-0042',
    orgnlInstrId: 'INSTR-0042',
    orgnlEndToEndId: E2E,
    orgnlTxId: 'TX-2026-0042',
    orgnlIntrBkSttlmAmt: { value: '1000.00', ccy: 'RWF' },
    rtrdIntrBkSttlmAmt: { value: '1000.00', ccy: 'RWF' },
    chrgBr: 'SLEV',
    rtrRsnInf: [{ rsn: { cd: 'AC04' }, addtlInf: 'Creditor account closed after settlement' }],
  }],
};

const PACS007_PAYLOAD = {
  grpHdr: {
    msgId: 'RVSL-MSG-0042',
    creDtTm: '2026-06-24T09:20:00Z',
    nbOfTxs: '1',
    sttlmInf: { sttlmMtd: 'CLRG' },
    instgAgt: { finInstnId: { othr: { id: '078' } } },
    instdAgt: { finInstnId: { othr: { id: '040' } } },
  },
  orgnlGrpInf: { orgnlMsgId: ORIG_MSG_ID, orgnlMsgNmId: 'pacs.008.001.11' },
  txInf: [{
    rvslId: 'RVSL-0042',
    orgnlInstrId: 'INSTR-0042',
    orgnlEndToEndId: E2E,
    orgnlTxId: 'TX-2026-0042',
    orgnlIntrBkSttlmAmt: { value: '1000.00', ccy: 'RWF' },
    rvsdIntrBkSttlmAmt: { value: '1000.00', ccy: 'RWF' },
    chrgBr: 'SLEV',
    rvslRsnInf: [{ rsn: { cd: 'DUPL' }, addtlInf: 'Duplicate submission of the same instruction' }],
  }],
};

/* ───────────────────────── Payment recall (camt.056 → camt.029 → pacs.004) ── */

const CAMT056_PAYLOAD = {
  assgnmt: {
    id: 'ASSGN-CXL-0042',
    assgnr: { agt: { finInstnId: { othr: { id: '078' } } } },
    assgne: { agt: { finInstnId: { othr: { id: '040' } } } },
    creDtTm: '2026-06-24T09:10:00Z',
  },
  undrlyg: [{
    orgnlGrpInfAndCxl: { orgnlMsgId: ORIG_MSG_ID, orgnlMsgNmId: 'pacs.008.001.11' },
    txInf: [{
      cxlId: 'CXL-0042',
      orgnlInstrId: 'INSTR-0042',
      orgnlEndToEndId: E2E,
      orgnlTxId: 'TX-2026-0042',
      cxlRsnInf: [{ rsn: { cd: 'CUST' }, addtlInf: 'Customer sent the payment in error' }],
    }],
  }],
};

const camt029For = (cxlSts, extras = {}) => ({
  assgnmt: {
    id: 'ASSGN-CXL-0042',
    assgnr: { agt: { finInstnId: { othr: { id: '040' } } } },
    assgne: { agt: { finInstnId: { othr: { id: '078' } } } },
    creDtTm: '2026-06-24T09:15:00Z',
  },
  rslvdCase: {
    id: 'ASSGN-CXL-0042',
    cretr: { agt: { finInstnId: { othr: { id: '078' } } } },
  },
  sts: { conf: 'ACCP' },
  cxlDtls: [{
    orgnlGrpInfAndSts: { orgnlMsgId: ORIG_MSG_ID, orgnlMsgNmId: 'pacs.008.001.11', grpCxlSts: cxlSts },
    txInfAndSts: [{
      cxlStsId: 'CXLSTS-0042',
      orgnlEndToEndId: E2E,
      orgnlTxId: 'TX-2026-0042',
      txCxlSts: cxlSts,
      ...extras,
    }],
  }],
});

const PACS004_AFTER_RECALL = {
  ...PACS004_PAYLOAD,
  txInf: [{
    ...PACS004_PAYLOAD.txInf[0],
    rtrRsnInf: [{ rsn: { cd: 'FOCR' }, addtlInf: 'Return following the accepted cancellation request' }],
  }],
};

/* ───────────────────────── Request-to-Pay ────────────────────────── */

const RTP_E2E = 'E2E-RTP-0042';

const PAIN013_PAYLOAD = {
  grpHdr: {
    msgId: 'PAIN013-RTP-0042',
    creDtTm: '2026-06-24T08:50:00Z',
    nbOfTxs: '1',
    initgPty: { nm: 'Rwanda Revenue Authority' },
  },
  pmtInf: [{
    pmtInfId: 'PMTINF-RTP-0042',
    pmtMtd: 'TRF',
    reqdExctnDt: '2026-06-25',
    dbtr: { nm: 'Alice Uwase' },
    dbtrAcct: { id: { othr: { id: '0788123456' } } },
    dbtrAgt: { finInstnId: { othr: { id: '078' } } },
    cdtTrfTx: [{
      pmtId: { endToEndId: RTP_E2E, instrId: 'INSTR-RTP-0042' },
      amt: { instdAmt: { value: '500.00', ccy: 'RWF' } },
      cdtrAgt: { finInstnId: { othr: { id: '040' } } },
      cdtr: { nm: 'Rwanda Revenue Authority', id: { orgId: { othr: { id: 'RRA-001' } } } },
      cdtrAcct: { id: { othr: { id: '0780000001' } }, ccy: 'RWF' },
      rmtInf: { ustrd: 'Tax assessment 2026-Q2 — TIN 123456789' },
    }],
  }],
};

const PAIN014_PAYLOAD = {
  grpHdr: {
    msgId: 'PAIN014-RTP-0042',
    creDtTm: '2026-06-24T08:55:00Z',
    nbOfTxs: '1',
    initgPty: { nm: 'Alice Uwase' },
  },
  orgnlGrpInfAndSts: {
    orgnlMsgId: 'PAIN013-RTP-0042',
    orgnlMsgNmId: 'pain.013.001.11',
    grpSts: 'ACCP',
  },
  orgnlPmtInfAndSts: [{
    orgnlPmtInfId: 'PMTINF-RTP-0042',
    pmtInfSts: 'ACCP',
    txInfAndSts: [{ orgnlEndToEndId: RTP_E2E, txSts: 'ACCP' }],
  }],
};

const RTP_PACS008 = {
  grpHdr: {
    msgId: 'MSG-RTP-PAY-0042',
    creDtTm: '2026-06-24T09:00:00Z',
    nbOfTxs: '1',
    sttlmInf: { sttlmMtd: 'CLRG' },
    instgAgt: { finInstnId: { othr: { id: '078' } } },
    instdAgt: { finInstnId: { othr: { id: '040' } } },
  },
  cdtTrfTxInf: [{
    pmtId: { instrId: 'INSTR-RTP-0042', endToEndId: RTP_E2E, txId: 'TX-RTP-0042' },
    intrBkSttlmAmt: { value: '500.00', ccy: 'RWF' },
    chrgBr: 'SLEV',
    dbtr: { nm: 'Alice Uwase' },
    dbtrAcct: { id: { othr: { id: '0788123456' } } },
    dbtrAgt: { finInstnId: { othr: { id: '078' } } },
    cdtrAgt: { finInstnId: { othr: { id: '040' } } },
    cdtr: { nm: 'Rwanda Revenue Authority' },
    cdtrAcct: { id: { othr: { id: '0780000001' } } },
  }],
};

const RTP_PACS002 = {
  grpHdr: {
    msgId: 'STS-RTP-0042',
    creDtTm: '2026-06-24T09:00:05Z',
    instgAgt: { finInstnId: { othr: { id: '040' } } },
    instdAgt: { finInstnId: { othr: { id: '078' } } },
  },
  orgnlGrpInfAndSts: { orgnlMsgId: 'MSG-RTP-PAY-0042', orgnlMsgNmId: 'pacs.008.001.11' },
  txInfAndSts: [{
    orgnlInstrId: 'INSTR-RTP-0042',
    orgnlEndToEndId: RTP_E2E,
    orgnlTxId: 'TX-RTP-0042',
    txSts: 'ACSC',
  }],
};

/* ── RTP cancellation (camt.056 referencing the pain.013 → camt.029) ──
 *
 * Market practice (aligned with the EPC SRTP rulebook's Request for
 * Cancellation): only the INITIATOR (the creditor) may cancel its own
 * Request-to-Pay, and only while it is still pending — presented but not yet
 * accepted or paid. At customer-to-bank level schemes carry this as camt.055;
 * what the corridor exchanges is the interbank leg: a camt.056 whose
 * OrgnlMsgNmId references the original pain.013, resolved by camt.029
 * (ACCR confirmed / RJCR rejected). After acceptance the remedy is no longer
 * cancellation but the payment recall flow (see Payments).
 */

const RTP_CXL_CASE = 'CASE-RTP-CXL-0042';

const RTP_CAMT056_CANCEL = {
  assgnmt: {
    id: RTP_CXL_CASE,
    assgnr: { agt: { finInstnId: { othr: { id: '040' } } } },   // creditor DFSP — the RtP initiator
    assgne: { agt: { finInstnId: { othr: { id: '078' } } } },   // debtor DFSP — holds the presentment
    creDtTm: '2026-06-24T08:53:00Z',
  },
  undrlyg: [{
    orgnlGrpInfAndCxl: { orgnlMsgId: 'PAIN013-RTP-0042', orgnlMsgNmId: 'pain.013.001.11' },
    txInf: [{
      cxlId: 'CXL-RTP-0042',
      orgnlInstrId: 'INSTR-RTP-0042',
      orgnlEndToEndId: RTP_E2E,
      cxlRsnInf: [{ rsn: { cd: 'DUPL' }, addtlInf: 'Assessment issued twice — this Request-to-Pay is withdrawn' }],
    }],
  }],
};

const rtpCamt029For = (cxlSts, extras = {}) => ({
  assgnmt: {
    id: RTP_CXL_CASE,
    assgnr: { agt: { finInstnId: { othr: { id: '078' } } } },
    assgne: { agt: { finInstnId: { othr: { id: '040' } } } },
    creDtTm: '2026-06-24T08:54:00Z',
  },
  rslvdCase: {
    id: RTP_CXL_CASE,
    cretr: { agt: { finInstnId: { othr: { id: '040' } } } },
  },
  sts: { conf: 'ACCP' },
  cxlDtls: [{
    orgnlGrpInfAndSts: { orgnlMsgId: 'PAIN013-RTP-0042', orgnlMsgNmId: 'pain.013.001.11', grpCxlSts: cxlSts },
    txInfAndSts: [{
      cxlStsId: 'CXLSTS-RTP-0042',
      orgnlEndToEndId: RTP_E2E,
      txCxlSts: cxlSts,
      ...extras,
    }],
  }],
});

/* ───────────────────────── Account lifecycle ─────────────────────── */

const ACMT023_PAYLOAD = {
  assgnmt: {
    msgId: 'ACMT023-VRF-0042',
    assgnr: { pty: { nm: 'Equity Bank Rwanda' } },
    assgne: { pty: { nm: 'Bank of Kigali' } },
  },
  vrfctn: { id: 'VRF-0720000042' },
};

const ACMT007_PAYLOAD = {
  references: {
    messageId: { id: 'ACMT007-REG-0042', creationDateTime: '2026-06-24T08:00:00Z' },
    processId: { id: 'PROC-REG-0042', creationDateTime: '2026-06-24T08:00:00Z' },
    attchdDocNm: 'incorporation-cert-0042',
  },
  account: {
    id: { othr: { id: 'ACC-0042' } },
    name: 'Umoja Traders Ltd',
    status: 'ENAB',
    currency: 'RWF',
  },
  acctSvcrId: { finInstnId: { othr: { id: '040' } } },
  organization: {
    fullLegalName: 'Umoja Traders Ltd',
    countryOfOperation: 'RW',
    registrationDate: '2020-03-01',
    lglAdr: { adrTp: { cd: 'ADDR' } },
    orgId: { othr: { id: 'ORG-UMOJA-0042' } },
  },
};

const ACMT003_PAYLOAD = {
  msgId: { id: 'ACMT003-MOD-0042', creDtTm: '2026-06-24T10:00:00Z' },
  instrDtls: { id: 'INSTR-MOD-0042', existingAccountId: 'ACC-0042' },
  invstmtAcctSelctn: { acctId: 'ACC-0042' },
  modfdAcctPties: {
    scopeIndication: 'UPDT',
    acctSvcrId: { finInstnId: { othr: { id: '040' } } },
    acctOwnr: { indvPrsn: { nm: 'Umoja Traders Ltd — Finance Office' } },
  },
};

const ACMT019_PAYLOAD = {
  refs: {
    msgId: { id: 'ACMT019-CLO-0042', creDtTm: '2026-06-24T11:00:00Z' },
    prcId: { id: 'PROC-CLO-0042', creDtTm: '2026-06-24T11:00:00Z' },
  },
  acctId: { id: { othr: { id: 'ACC-0042' } }, nm: 'Umoja Traders Ltd', ccy: 'RWF' },
  acctSvcrId: { finInstnId: { othr: { id: '040' } } },
  orgId: { fullLegalName: 'Umoja Traders Ltd', ctryOfOpr: 'RW' },
};

const ACMT020_PAYLOAD = {
  refs: {
    msgId: { id: 'ACMT020-AMD-0042' },
    prcId: { id: 'PROC-CLO-0042' },
  },
  acctId: { id: { iban: 'RW42040000000000ACC0042' }, ccy: 'RWF' },
  acctSvcrId: { finInstnId: { nm: 'Bank of Kigali', othr: { id: '040' } } },
  orgId: { othr: { id: 'ORG-UMOJA-0042' } },
};

const acmt006For = (relatedRef) => ({
  msgId: { id: 'ACMT006-STS-0042' },
  rltdRef: { ref: relatedRef },
  stsRpt: { sts: { sts: 'RECE' } },
});

/* ───────────────────────── Scenarios ─────────────────────────────── */

export const FLOW_SCENARIOS = {
  'credit-happy': {
    id: 'credit-happy',
    category: 'Payments',
    label: 'Credit transfer · accepted',
    description: 'A successful customer credit transfer settled through the switch.',
    correlationId: E2E,
    steps: [
      {
        from: 0, to: 1, msgType: 'pacs.008', kind: 'req',
        title: 'Payment instruction sent',
        description: "Alice's bank instructs the switch to move 1,000 RWF to Bob. The EndToEndId is minted here and carried by every later message.",
        corrNote: 'minted here',
        payload: PACS008_PAYLOAD,
      },
      {
        from: 1, to: 2, msgType: 'pacs.008', kind: 'req',
        title: 'Switch routes to payee bank',
        description: "The switch validates signature and XSD, then forwards the same credit transfer to Bob's bank.",
        corrNote: 'forwarded unchanged',
        sameAs: 0,
      },
      {
        from: 2, to: 1, msgType: 'pacs.002', kind: 'ok',
        title: 'Payee bank confirms (ACSC)',
        description: "Bob's bank credits Bob and returns a status report. OrgnlEndToEndId echoes the original payment.",
        corrNote: 'echoed as OrgnlEndToEndId',
        payload: pacs002For('ACSC'),
      },
      {
        from: 1, to: 0, msgType: 'pacs.002', kind: 'ok',
        title: 'Confirmation relayed — funds settle',
        description: 'The switch relays the ACSC status back to the payer bank. The transfer is complete.',
        corrNote: 'echoed as OrgnlEndToEndId',
        sameAs: 2,
      },
    ],
  },

  'credit-reject': {
    id: 'credit-reject',
    category: 'Payments',
    label: 'Credit transfer · rejected',
    description: 'The payee bank rejects the transfer; a pacs.002 RJCT carries the reason back.',
    correlationId: E2E,
    steps: [
      {
        from: 0, to: 1, msgType: 'pacs.008', kind: 'req',
        title: 'Payment instruction sent',
        description: "Alice's bank submits the same credit transfer. The EndToEndId is minted here.",
        corrNote: 'minted here',
        payload: PACS008_PAYLOAD,
      },
      {
        from: 1, to: 2, msgType: 'pacs.008', kind: 'req',
        title: 'Switch routes to payee bank',
        description: "The switch forwards the credit transfer to Bob's bank for application.",
        corrNote: 'forwarded unchanged',
        sameAs: 0,
      },
      {
        from: 2, to: 1, msgType: 'pacs.002', kind: 'rjct',
        title: 'Payee bank rejects (RJCT)',
        description: "Bob's account is closed, so his bank returns a rejection with reason code AC04. OrgnlEndToEndId still links back to the original payment.",
        corrNote: 'echoed as OrgnlEndToEndId',
        payload: pacs002For('RJCT', { stsRsnInf: { rsn: { cd: 'AC04' }, addtlInf: 'Creditor account closed' } }),
      },
      {
        from: 1, to: 0, msgType: 'pacs.002', kind: 'rjct',
        title: 'Rejection relayed to payer',
        description: "The switch relays the RJCT status back to Alice's bank. No funds move.",
        corrNote: 'echoed as OrgnlEndToEndId',
        sameAs: 2,
      },
    ],
  },

  'payment-return': {
    id: 'payment-return',
    category: 'Payments',
    label: 'Payment return',
    description: 'Payee-initiated: the payee bank returns unapplied funds on its own (pacs.004, no recall needed) — e.g. the account was closed after settlement. For the payer-initiated recall choreography, see Payment recall.',
    correlationId: E2E,
    steps: [
      {
        from: 2, to: 1, msgType: 'pacs.004', kind: 'warn',
        title: 'Payee bank returns the funds',
        description: "Days after settlement, Bob's bank discovers the account is closed and returns the full 1,000 RWF with reason AC04. OrgnlEndToEndId points at the settled payment.",
        corrNote: 'echoed as OrgnlEndToEndId',
        payload: PACS004_PAYLOAD,
      },
      {
        from: 1, to: 0, msgType: 'pacs.004', kind: 'warn',
        title: 'Switch routes the return',
        description: "The switch forwards the payment return to Alice's bank, which will re-credit Alice.",
        corrNote: 'forwarded unchanged',
        sameAs: 0,
      },
      {
        from: 0, to: 1, msgType: 'pacs.002', kind: 'ok',
        title: 'Payer bank acknowledges the return',
        description: "Alice's bank accepts the returned funds and reports ACSC for the return transaction.",
        corrNote: 'echoed as OrgnlEndToEndId',
        payload: pacs002For('ACSC'),
      },
      {
        from: 1, to: 2, msgType: 'pacs.002', kind: 'ok',
        title: 'Acknowledgement relayed',
        description: 'The switch relays the acknowledgement; the return is settled in both ledgers.',
        corrNote: 'echoed as OrgnlEndToEndId',
        sameAs: 2,
      },
    ],
  },

  'payment-recall': {
    id: 'payment-recall',
    category: 'Payments',
    label: 'Payment recall · accepted',
    description: 'Payer-initiated recall: camt.056 asks for the money back, camt.029 accepts (ACCR), and pacs.004 returns the funds — the full three-message choreography.',
    correlationId: E2E,
    steps: [
      {
        from: 0, to: 1, msgType: 'camt.056', kind: 'req',
        title: 'Payer bank requests cancellation',
        description: "Alice realises she paid the wrong person. Her bank sends an FI-to-FI cancellation request (reason CUST) for the settled payment. The case is assigned to Bob's bank; OrgnlEndToEndId identifies the payment to recall.",
        corrNote: 'echoed as OrgnlEndToEndId',
        payload: CAMT056_PAYLOAD,
      },
      {
        from: 1, to: 2, msgType: 'camt.056', kind: 'req',
        title: 'Switch routes the recall request',
        description: "The switch forwards the cancellation request to Bob's bank, which must investigate whether the funds can still be recovered.",
        corrNote: 'forwarded unchanged',
        sameAs: 0,
      },
      {
        from: 2, to: 1, msgType: 'camt.029', kind: 'ok',
        title: 'Payee bank accepts the recall (ACCR)',
        description: "The funds are still on Bob's account, so his bank resolves the investigation in favour of cancellation: the resolved case echoes the assignment id and the cancellation status is ACCR.",
        corrNote: 'case id + OrgnlEndToEndId',
        payload: camt029For('ACCR'),
      },
      {
        from: 1, to: 0, msgType: 'camt.029', kind: 'ok',
        title: 'Resolution relayed to payer bank',
        description: "Alice's bank now knows the recall was accepted and funds are coming back.",
        corrNote: 'case id + OrgnlEndToEndId',
        sameAs: 2,
      },
      {
        from: 2, to: 1, msgType: 'pacs.004', kind: 'ok',
        title: 'Funds returned (FOCR)',
        description: "Bob's bank executes the agreed return: a pacs.004 with reason FOCR — Following Cancellation Request — moves the 1,000 RWF back.",
        corrNote: 'echoed as OrgnlEndToEndId',
        payload: PACS004_AFTER_RECALL,
      },
      {
        from: 1, to: 0, msgType: 'pacs.004', kind: 'ok',
        title: 'Return routed — recall complete',
        description: "The switch forwards the return; Alice's bank re-credits her. Request, resolution and return all carry the same EndToEndId.",
        corrNote: 'echoed as OrgnlEndToEndId',
        sameAs: 4,
      },
    ],
  },

  'payment-recall-refused': {
    id: 'payment-recall-refused',
    category: 'Payments',
    label: 'Payment recall · refused',
    description: 'The payee bank refuses the recall (camt.029 RJCR) — the funds are already gone.',
    correlationId: E2E,
    steps: [
      {
        from: 0, to: 1, msgType: 'camt.056', kind: 'req',
        title: 'Payer bank requests cancellation',
        description: "Alice's bank asks for the settled payment back (reason CUST — sent in error).",
        corrNote: 'echoed as OrgnlEndToEndId',
        payload: CAMT056_PAYLOAD,
      },
      {
        from: 1, to: 2, msgType: 'camt.056', kind: 'req',
        title: 'Switch routes the recall request',
        description: "The switch forwards the cancellation request to Bob's bank.",
        corrNote: 'forwarded unchanged',
        sameAs: 0,
      },
      {
        from: 2, to: 1, msgType: 'camt.029', kind: 'rjct',
        title: 'Payee bank refuses (RJCR)',
        description: 'Bob has already withdrawn the money. His bank resolves the investigation with RJCR and reason AM04 — no pacs.004 will follow; recovery moves outside the scheme.',
        corrNote: 'case id + OrgnlEndToEndId',
        payload: camt029For('RJCR', { cxlStsRsnInf: { rsn: { cd: 'AM04' }, addtlInf: 'Funds already withdrawn by the beneficiary' } }),
      },
      {
        from: 1, to: 0, msgType: 'camt.029', kind: 'rjct',
        title: 'Refusal relayed to payer bank',
        description: "Alice's bank informs her the recall failed. The camt.029 closes the case either way — that is the value of the choreography.",
        corrNote: 'case id + OrgnlEndToEndId',
        sameAs: 2,
      },
    ],
  },

  'payment-reversal': {
    id: 'payment-reversal',
    category: 'Payments',
    label: 'Payment reversal',
    description: 'The payer bank reverses its own instruction (pacs.007) — e.g. a duplicate submission.',
    correlationId: E2E,
    steps: [
      {
        from: 0, to: 1, msgType: 'pacs.007', kind: 'warn',
        title: 'Payer bank requests reversal',
        description: "Alice's bank detects it submitted the same instruction twice and sends a reversal (reason DUPL) for the duplicate. OrgnlEndToEndId identifies the payment to unwind.",
        corrNote: 'echoed as OrgnlEndToEndId',
        payload: PACS007_PAYLOAD,
      },
      {
        from: 1, to: 2, msgType: 'pacs.007', kind: 'warn',
        title: 'Switch routes the reversal',
        description: "The switch forwards the reversal to Bob's bank.",
        corrNote: 'forwarded unchanged',
        sameAs: 0,
      },
      {
        from: 2, to: 1, msgType: 'pacs.002', kind: 'ok',
        title: 'Payee bank accepts the reversal',
        description: "Bob's bank debits the duplicate credit and confirms with ACSC.",
        corrNote: 'echoed as OrgnlEndToEndId',
        payload: pacs002For('ACSC'),
      },
      {
        from: 1, to: 0, msgType: 'pacs.002', kind: 'ok',
        title: 'Confirmation relayed',
        description: 'The duplicate is unwound; both ledgers are square again.',
        corrNote: 'echoed as OrgnlEndToEndId',
        sameAs: 2,
      },
    ],
  },

  'account-lookup': {
    id: 'account-lookup',
    category: 'Accounts',
    label: 'Account lookup · verified',
    description: 'Confirmation of payee: verify who holds an account before paying (acmt.023 → acmt.024).',
    correlationId: 'VRF-0720000042',
    actors: LOOKUP_ACTORS,
    steps: [
      {
        from: 0, to: 1, msgType: 'acmt.023', kind: 'req',
        title: 'Verification request sent',
        description: "Before Alice pays Bob, her bank asks: who holds account 0720000042? The verification id is minted here.",
        corrNote: 'minted here',
        payload: ACMT023_PAYLOAD,
      },
      {
        from: 1, to: 2, msgType: 'acmt.023', kind: 'req',
        title: 'Switch routes to account holder',
        description: "The switch forwards the identification verification request to Bank of Kigali.",
        corrNote: 'forwarded unchanged',
        sameAs: 0,
      },
      {
        from: 2, to: 1, msgType: 'acmt.024', kind: 'ok',
        title: 'Account holder confirms',
        description: 'Bank of Kigali verifies the account and reports a positive match. OrgnlId references the verification request.',
        corrNote: 'echoed as OrgnlId',
        payload: { msgId: 'ACMT024-VRF-0042', assgnr: '040', assgne: '078', orgnlId: 'ACMT023-VRF-0042', verified: true },
      },
      {
        from: 1, to: 0, msgType: 'acmt.024', kind: 'ok',
        title: 'Verification relayed',
        description: "Alice's bank shows her the confirmed payee name before she commits the payment.",
        corrNote: 'echoed as OrgnlId',
        sameAs: 2,
      },
    ],
  },

  'account-lookup-nomatch': {
    id: 'account-lookup-nomatch',
    category: 'Accounts',
    label: 'Account lookup · no match',
    description: 'The verification comes back negative — the payer sees a warning before any money moves.',
    correlationId: 'VRF-0720000042',
    actors: LOOKUP_ACTORS,
    steps: [
      {
        from: 0, to: 1, msgType: 'acmt.023', kind: 'req',
        title: 'Verification request sent',
        description: "Alice's bank asks the switch to verify account 0720000042 before paying.",
        corrNote: 'minted here',
        payload: ACMT023_PAYLOAD,
      },
      {
        from: 1, to: 2, msgType: 'acmt.023', kind: 'req',
        title: 'Switch routes to account holder',
        description: 'The switch forwards the request to the account-holding bank.',
        corrNote: 'forwarded unchanged',
        sameAs: 0,
      },
      {
        from: 2, to: 1, msgType: 'acmt.024', kind: 'rjct',
        title: 'No match',
        description: 'The account does not exist (or the name does not match). The report says verified = false.',
        corrNote: 'echoed as OrgnlId',
        payload: { msgId: 'ACMT024-VRF-0042', assgnr: '040', assgne: '078', orgnlId: 'ACMT023-VRF-0042', verified: false },
      },
      {
        from: 1, to: 0, msgType: 'acmt.024', kind: 'rjct',
        title: 'Warning relayed to payer',
        description: "Alice's bank warns her before any payment is attempted — fraud averted, no funds at risk.",
        corrNote: 'echoed as OrgnlId',
        sameAs: 2,
      },
    ],
  },

  'account-registration': {
    id: 'account-registration',
    category: 'Accounts',
    label: 'Account registration · approved',
    description: 'A participant registers a new account with the switch (acmt.007 → acmt.010 + acmt.014).',
    correlationId: 'ACMT007-REG-0042',
    actors: ACCOUNT_ACTORS,
    steps: [
      {
        from: 0, to: 1, msgType: 'acmt.007', kind: 'req',
        title: 'Account opening request',
        description: 'Bank of Kigali registers a new account for Umoja Traders Ltd with the switch. The message id anchors the registration process.',
        corrNote: 'minted here',
        payload: ACMT007_PAYLOAD,
      },
      {
        from: 1, to: 0, msgType: 'acmt.010', kind: 'ok',
        title: 'Switch acknowledges (approval)',
        description: 'The switch validates the registration and acknowledges it — the account request is approved.',
        corrNote: 'process id links back',
        payload: { msgId: 'ACMT007-REG-0042', orgId: 'ORG-UMOJA-0042', acctSvcr: '040' },
      },
      {
        from: 1, to: 0, msgType: 'acmt.014', kind: 'ok',
        title: 'Account report issued',
        description: 'The switch follows up with an account report for the newly registered organisation — the account is live in the directory.',
        corrNote: 'process id links back',
        payload: { msgId: 'ACMT014-REG-0042', acctSvcr: '040', fullLglNm: 'Umoja Traders Ltd', orgId: 'ORG-UMOJA-0042' },
      },
    ],
  },

  'account-registration-reject': {
    id: 'account-registration-reject',
    category: 'Accounts',
    label: 'Account registration · rejected',
    description: 'The switch rejects the registration (acmt.011) — e.g. failed business validation.',
    correlationId: 'ACMT007-REG-0042',
    actors: ACCOUNT_ACTORS,
    steps: [
      {
        from: 0, to: 1, msgType: 'acmt.007', kind: 'req',
        title: 'Account opening request',
        description: 'Bank of Kigali submits the registration for Umoja Traders Ltd.',
        corrNote: 'minted here',
        payload: ACMT007_PAYLOAD,
      },
      {
        from: 1, to: 0, msgType: 'acmt.011', kind: 'rjct',
        title: 'Switch rejects the registration',
        description: 'Business validation fails (e.g. incomplete KYC documentation) and the switch returns an account request rejection.',
        corrNote: 'process id links back',
        payload: { msgId: 'ACMT007-REG-0042', orgId: 'ORG-UMOJA-0042', acctSvcr: '040' },
      },
    ],
  },

  'account-modification': {
    id: 'account-modification',
    category: 'Accounts',
    label: 'Account modification',
    description: 'A participant modifies details of a registered account (acmt.003 → acmt.006).',
    correlationId: 'ACMT003-MOD-0042',
    actors: ACCOUNT_ACTORS,
    steps: [
      {
        from: 0, to: 1, msgType: 'acmt.003', kind: 'req',
        title: 'Modification instruction sent',
        description: 'Bank of Kigali updates the account owner details for ACC-0042 (scope UPDT).',
        corrNote: 'minted here',
        payload: ACMT003_PAYLOAD,
      },
      {
        from: 1, to: 0, msgType: 'acmt.006', kind: 'ok',
        title: 'Status report: received',
        description: 'The switch confirms receipt of the modification. RltdRef echoes the acmt.003 message id while the change is applied.',
        corrNote: 'echoed as RltdRef',
        payload: acmt006For('ACMT003-MOD-0042'),
      },
    ],
  },

  'account-closing': {
    id: 'account-closing',
    category: 'Accounts',
    label: 'Account closing',
    description: 'Close an account, amend the pending closure, and get the status (acmt.019 → acmt.020 → acmt.006).',
    correlationId: 'PROC-CLO-0042',
    actors: ACCOUNT_ACTORS,
    steps: [
      {
        from: 0, to: 1, msgType: 'acmt.019', kind: 'req',
        title: 'Closing request sent',
        description: 'Umoja Traders is winding down; Bank of Kigali requests closure of ACC-0042. The process id anchors the closure workflow.',
        corrNote: 'minted here (PrcId)',
        payload: ACMT019_PAYLOAD,
      },
      {
        from: 0, to: 1, msgType: 'acmt.020', kind: 'req',
        title: 'Closure amended',
        description: 'Before execution, the bank amends the pending closure with the settlement account for the residual balance transfer — same PrcId, so the switch knows which closure to amend.',
        corrNote: 'same PrcId',
        payload: ACMT020_PAYLOAD,
      },
      {
        from: 1, to: 0, msgType: 'acmt.006', kind: 'ok',
        title: 'Status report: received',
        description: 'The switch reports the amended closure is received and in progress. RltdRef points at the closing request.',
        corrNote: 'echoed as RltdRef',
        payload: acmt006For('ACMT019-CLO-0042'),
      },
    ],
  },

  'request-to-pay': {
    id: 'request-to-pay',
    category: 'Request-to-Pay',
    label: 'Request-to-Pay · full loop',
    description: 'The creditor requests payment (pain.013), the debtor accepts (pain.014), and the actual credit transfer settles it (pacs.008 → pacs.002).',
    correlationId: RTP_E2E,
    actors: RTP_ACTORS,
    steps: [
      {
        from: 2, to: 1, msgType: 'pain.013', kind: 'req',
        title: 'Creditor requests payment',
        description: 'RRA (via its bank) asks Alice to pay her 500 RWF tax assessment. The RtP EndToEndId is minted here.',
        corrNote: 'minted here',
        payload: PAIN013_PAYLOAD,
      },
      {
        from: 1, to: 0, msgType: 'pain.013', kind: 'req',
        title: 'Switch routes the request',
        description: "The switch forwards the Request-to-Pay to Alice's bank, which presents it in her app.",
        corrNote: 'forwarded unchanged',
        sameAs: 0,
      },
      {
        from: 0, to: 1, msgType: 'pain.014', kind: 'ok',
        title: 'Debtor accepts (ACCP)',
        description: 'Alice approves the request. Her bank returns a creditor payment activation status of ACCP referencing the original pain.013.',
        corrNote: 'echoed as OrgnlEndToEndId',
        payload: PAIN014_PAYLOAD,
      },
      {
        from: 1, to: 2, msgType: 'pain.014', kind: 'ok',
        title: 'Acceptance relayed to creditor',
        description: 'RRA now knows the payment is coming.',
        corrNote: 'echoed as OrgnlEndToEndId',
        sameAs: 2,
      },
      {
        from: 0, to: 1, msgType: 'pacs.008', kind: 'req',
        title: 'Payment executes',
        description: "Alice's bank initiates the actual credit transfer, reusing the SAME EndToEndId as the Request-to-Pay — this is the thread that ties request and settlement together.",
        corrNote: 'same EndToEndId as the RtP',
        payload: RTP_PACS008,
      },
      {
        from: 1, to: 2, msgType: 'pacs.008', kind: 'req',
        title: 'Switch routes the payment',
        description: "The switch forwards the credit transfer to RRA's bank.",
        corrNote: 'forwarded unchanged',
        sameAs: 4,
      },
      {
        from: 2, to: 1, msgType: 'pacs.002', kind: 'ok',
        title: 'Creditor bank confirms (ACSC)',
        description: "RRA's bank credits the collections account and confirms settlement.",
        corrNote: 'echoed as OrgnlEndToEndId',
        payload: RTP_PACS002,
      },
      {
        from: 1, to: 0, msgType: 'pacs.002', kind: 'ok',
        title: 'Confirmation relayed — loop closed',
        description: 'The RtP lifecycle completes: requested, accepted, paid, confirmed — one EndToEndId end to end.',
        corrNote: 'echoed as OrgnlEndToEndId',
        sameAs: 6,
      },
    ],
  },

  'rtp-cancellation': {
    id: 'rtp-cancellation',
    category: 'Request-to-Pay',
    label: 'RtP cancellation · confirmed',
    description: 'The creditor withdraws a pending Request-to-Pay before the debtor acts: camt.056 referencing the original pain.013, confirmed by camt.029 (ACCR). Only the initiator may cancel, and only while the request is still pending.',
    correlationId: RTP_E2E,
    actors: RTP_ACTORS,
    steps: [
      {
        from: 2, to: 1, msgType: 'camt.056', kind: 'req',
        title: 'Creditor cancels its Request-to-Pay',
        description: 'RRA discovers the tax assessment was issued twice and withdraws the pending request before Alice pays. Its bank sends an FI-to-FI cancellation whose OrgnlMsgNmId references the ORIGINAL pain.013, with reason DUPL. (At customer-to-bank level schemes carry this as camt.055; the corridor exchanges the interbank leg.)',
        corrNote: 'echoed as OrgnlEndToEndId',
        payload: RTP_CAMT056_CANCEL,
      },
      {
        from: 1, to: 0, msgType: 'camt.056', kind: 'req',
        title: 'Switch validates state and routes the cancellation',
        description: "The switch enforces the two golden rules before forwarding to Alice's bank: the canceller must be the request's initiator, and the request must still be pending — presented, but not yet accepted or paid. State gating at the switch is what resolves a cancellation and an acceptance crossing mid-flight: first state change wins.",
        corrNote: 'forwarded unchanged',
        sameAs: 0,
      },
      {
        from: 0, to: 1, msgType: 'camt.029', kind: 'ok',
        title: 'Debtor bank confirms the cancellation (ACCR)',
        description: "Alice had not acted yet. Her bank atomically withdraws the presentment from her app — so she can no longer accept a request that no longer exists — marks the RtP CANCELLED, and resolves the case with cancellation status ACCR.",
        corrNote: 'case id + OrgnlEndToEndId',
        payload: rtpCamt029For('ACCR'),
      },
      {
        from: 1, to: 2, msgType: 'camt.029', kind: 'ok',
        title: 'Confirmation relayed — request withdrawn',
        description: "RRA's bank marks the RtP CANCELLED too. No pain.014 and no payment will follow, and both sides retain the full audit trail — request, cancellation and resolution all share one EndToEndId.",
        corrNote: 'case id + OrgnlEndToEndId',
        sameAs: 2,
      },
    ],
  },

  'rtp-cancellation-late': {
    id: 'rtp-cancellation-late',
    category: 'Request-to-Pay',
    label: 'RtP cancellation · too late',
    description: 'The debtor accepted moments before the cancellation arrived: the request is rejected (camt.029 RJCR) and recovery, if truly needed, becomes a payment recall.',
    correlationId: RTP_E2E,
    actors: RTP_ACTORS,
    steps: [
      {
        from: 2, to: 1, msgType: 'camt.056', kind: 'req',
        title: 'Creditor cancels its Request-to-Pay',
        description: 'RRA tries to withdraw the request — but Alice has just tapped Accept in her app. Two state changes are now racing toward the switch.',
        corrNote: 'echoed as OrgnlEndToEndId',
        payload: RTP_CAMT056_CANCEL,
      },
      {
        from: 1, to: 0, msgType: 'camt.056', kind: 'req',
        title: 'Switch routes the cancellation',
        description: "The acceptance reached the switch first: a pain.014 ACCP is already in flight and the credit transfer is executing. The switch still forwards the cancellation — the party that owns the state (the debtor side) gives the authoritative answer.",
        corrNote: 'forwarded unchanged',
        sameAs: 0,
      },
      {
        from: 0, to: 1, msgType: 'camt.029', kind: 'rjct',
        title: 'Too late — cancellation rejected (RJCR)',
        description: "First state change wins: the RtP is already ACCEPTED, so it can no longer be cancelled. Alice's bank resolves the case with RJCR and a reason explaining that the payment has been executed. Nothing is silently clawed back — an accepted payment is never unwound by a cancellation.",
        corrNote: 'case id + OrgnlEndToEndId',
        payload: rtpCamt029For('RJCR', { cxlStsRsnInf: { rsn: { cd: 'ARDT' }, addtlInf: 'Request already accepted; the credit transfer has been executed' } }),
      },
      {
        from: 1, to: 2, msgType: 'camt.029', kind: 'rjct',
        title: 'Rejection relayed — recover via recall',
        description: "RRA now knows the cancellation failed and the payment is coming. If the money truly must go back, the remedy is no longer cancellation but the payment recall choreography — camt.056 against the settled pacs.008 (see Payments → Payment recall).",
        corrNote: 'case id + OrgnlEndToEndId',
        sameAs: 2,
      },
    ],
  },
};

export const SCENARIO_ORDER = [
  'credit-happy', 'credit-reject',
  'payment-recall', 'payment-recall-refused',
  'payment-return', 'payment-reversal',
  'account-lookup', 'account-lookup-nomatch',
  'account-registration', 'account-registration-reject',
  'account-modification', 'account-closing',
  'request-to-pay', 'rtp-cancellation', 'rtp-cancellation-late',
];

export const SCENARIO_GROUPS = ['Payments', 'Accounts', 'Request-to-Pay'];
